import React, { useState, useEffect } from "react";
import { isEmpty } from "lodash";

import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
  Spinner,
} from "reactstrap";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

//redux
import { useSelector, useDispatch } from "react-redux";

import { createSelector } from "reselect";
import BreadCrumb from "../../Components/Common/BreadCrumb";
// API helper
import { APIClient } from "../../helpers/api_helper";

const CompanyProfile = () => {
  const dispatch: any = useDispatch();

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [companyData, setCompanyData] = useState<any>(null);

  const apiClient = new APIClient();

  // API functions
  const fetchCompanyData = async () => {
    setLoading(true);
    setApiError(null);
    
    try {
      const response = await apiClient.get('/user/company');
      const data = response.data || response;
      
      // Update state with fetched data
      if (data) {
        setCompanyData(data);
        
        // Reset form with new data
        validation.setValues({
          clientNumber: data.clientNumber || "",
          companyShortName: data.companyShortName || "",
          registerDate: data.registerDate || "",
          businessName: data.businessName || "",
          businessNo: data.businessNo || "",
          adminUserId: data.adminUserId || "",
          contactName: data.contactName || "",
          contactEmail: data.contactEmail || "",
          contactPhoneNo: data.contactPhoneNo || "",
          addressLine1: data.addressLine1 || "",
          addressLine2: data.addressLine2 || "",
          city: data.city || "",
          postalCode: data.postalCode || "",
          state: data.state || "",
          country: data.country || "",
        });
      }
    } catch (error: any) {
      console.error('Error fetching company data:', error);
      setApiError(error.response?.data?.message || 'Failed to fetch company data');
    } finally {
      setLoading(false);
    }
  };

  const updateCompanyData = async (formData: any) => {
    setSaving(true);
    setApiError(null);
    setSuccessMessage(null);
    
    try {
      // Prepare data for API (exclude read-only fields)
      const updateData = {
        companyShortName: formData.companyShortName,
        businessName: formData.businessName,
        businessNo: formData.businessNo,
        adminUserId: formData.adminUserId,
        contactName: formData.contactName,
        contactEmail: formData.contactEmail,
        contactPhoneNo: formData.contactPhoneNo,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        city: formData.city,
        postalCode: formData.postalCode,
        state: formData.state,
        country: formData.country,
      };
      
      const response = await apiClient.put('/user/company', updateData);
      
      if (response) {
        setSuccessMessage('Company profile updated successfully!');
        // Refresh data
        await fetchCompanyData();
      }
    } catch (error: any) {
      console.error('Error updating company data:', error);
      setApiError(error.response?.data?.message || 'Failed to update company profile');
    } finally {
      setSaving(false);
    }
  };

  // Fetch company data on component mount
  useEffect(() => {
    fetchCompanyData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const validation: any = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      clientNumber: "",
      companyShortName: "",
      registerDate: "",
      businessName: "",
      businessNo: "",
      adminUserId: "",
      contactName: "",
      contactEmail: "",
      contactPhoneNo: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      postalCode: "",
      state: "",
      country: "",
    },
    validationSchema: Yup.object({
      companyShortName: Yup.string().required("Please enter company short name"),
      businessName: Yup.string().required("Please enter business name"),
      contactEmail: Yup.string().email("Please enter a valid email address"),
      contactPhoneNo: Yup.string(),
      businessNo: Yup.string(),
      adminUserId: Yup.string(),
      contactName: Yup.string(),
      addressLine1: Yup.string(),
      addressLine2: Yup.string(),
      city: Yup.string(),
      postalCode: Yup.string(),
      state: Yup.string(),
      country: Yup.string(),
    }),
    onSubmit: async (values) => {
      await updateCompanyData(values);
    }
  });

  document.title = "Company Profile | Velzon - React Admin & Dashboard Template";
  
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Company Profile" pageTitle="Settings" />
          
          <Row>
            <Col lg="12">
              {apiError && <Alert color="danger">{apiError}</Alert>}
              {successMessage && <Alert color="success">{successMessage}</Alert>}

              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">Company Information</h4>
                  
                  {loading ? (
                    <div className="text-center p-4">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading company data...</p>
                    </div>
                  ) : (
                    <Form
                      className="form-horizontal"
                      onSubmit={(e: any) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      {/* Company Information Section - 3 columns per row */}
                      <div className="mb-4">
                        <h5 className="mb-3">Company Information</h5>
                        <Row>
                          <Col lg={4}>
                            {/* Client Number - Read Only */}
                            <div className="mb-3">
                              <Label className="form-label">Client Number</Label>
                              <Input
                                name="clientNumber"
                                className="form-control"
                                type="text"
                                value={validation.values.clientNumber || ""}
                                readOnly
                                style={{ backgroundColor: '#f8f9fa' }}
                              />
                            </div>
                          </Col>

                          <Col lg={4}>
                            {/* Register Date - Read Only */}
                            <div className="mb-3">
                              <Label className="form-label">Register Date</Label>
                              <Input
                                name="registerDate"
                                className="form-control"
                                type="text"
                                value={validation.values.registerDate || ""}
                                readOnly
                                style={{ backgroundColor: '#f8f9fa' }}
                              />
                            </div>
                          </Col>

                          <Col lg={4}>
                            {/* Company Short Name */}
                            <div className="mb-3">
                              <Label className="form-label">Company Short Name <span className="text-danger">*</span></Label>
                              <Input
                                name="companyShortName"
                                className="form-control"
                                placeholder="Enter company short name"
                                type="text"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.companyShortName || ""}
                                invalid={
                                  validation.touched.companyShortName && validation.errors.companyShortName ? true : false
                                }
                              />
                              {validation.touched.companyShortName && validation.errors.companyShortName ? (
                                <FormFeedback type="invalid">{validation.errors.companyShortName}</FormFeedback>
                              ) : null}
                            </div>
                          </Col>
                        </Row>

                        <Row>
                          <Col lg={4}>
                            {/* Business Name */}
                            <div className="mb-3">
                              <Label className="form-label">Business Name <span className="text-danger">*</span></Label>
                              <Input
                                name="businessName"
                                className="form-control"
                                placeholder="Enter business name"
                                type="text"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.businessName || ""}
                                invalid={
                                  validation.touched.businessName && validation.errors.businessName ? true : false
                                }
                              />
                              {validation.touched.businessName && validation.errors.businessName ? (
                                <FormFeedback type="invalid">{validation.errors.businessName}</FormFeedback>
                              ) : null}
                            </div>
                          </Col>

                          <Col lg={4}>
                            {/* Business Number */}
                            <div className="mb-3">
                              <Label className="form-label">Business Number</Label>
                              <Input
                                name="businessNo"
                                className="form-control"
                                placeholder="Enter business number"
                                type="text"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.businessNo || ""}
                                invalid={
                                  validation.touched.businessNo && validation.errors.businessNo ? true : false
                                }
                              />
                              {validation.touched.businessNo && validation.errors.businessNo ? (
                                <FormFeedback type="invalid">{validation.errors.businessNo}</FormFeedback>
                              ) : null}
                            </div>
                          </Col>

                          <Col lg={4}>
                            {/* Admin User ID */}
                            <div className="mb-3">
                              <Label className="form-label">Admin User ID</Label>
                              <Input
                                name="adminUserId"
                                className="form-control"
                                placeholder="Enter admin user ID"
                                type="text"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.adminUserId || ""}
                                invalid={
                                  validation.touched.adminUserId && validation.errors.adminUserId ? true : false
                                }
                              />
                              {validation.touched.adminUserId && validation.errors.adminUserId ? (
                                <FormFeedback type="invalid">{validation.errors.adminUserId}</FormFeedback>
                              ) : null}
                            </div>
                          </Col>
                        </Row>
                      </div>

                      {/* Business Address & Contact Info Section - Side by side */}
                      <Row>
                        <Col lg={6}>
                          {/* Business Address Section - 2 columns per row */}
                          <div className="mb-4">
                            <h5 className="mb-3">Business Address</h5>
                            
                            <Row>
                              <Col lg={12}>
                                {/* Address Line 1 */}
                                <div className="mb-3">
                                  <Label className="form-label">Address Line 1</Label>
                                  <Input
                                    name="addressLine1"
                                    className="form-control"
                                    placeholder="Enter address line 1"
                                    type="text"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.addressLine1 || ""}
                                    invalid={
                                      validation.touched.addressLine1 && validation.errors.addressLine1 ? true : false
                                    }
                                  />
                                  {validation.touched.addressLine1 && validation.errors.addressLine1 ? (
                                    <FormFeedback type="invalid">{validation.errors.addressLine1}</FormFeedback>
                                  ) : null}
                                </div>
                              </Col>
                            </Row>

                            <Row>
                              <Col lg={12}>
                                {/* Address Line 2 */}
                                <div className="mb-3">
                                  <Label className="form-label">Address Line 2</Label>
                                  <Input
                                    name="addressLine2"
                                    className="form-control"
                                    placeholder="Enter address line 2"
                                    type="text"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.addressLine2 || ""}
                                    invalid={
                                      validation.touched.addressLine2 && validation.errors.addressLine2 ? true : false
                                    }
                                  />
                                  {validation.touched.addressLine2 && validation.errors.addressLine2 ? (
                                    <FormFeedback type="invalid">{validation.errors.addressLine2}</FormFeedback>
                                  ) : null}
                                </div>
                              </Col>
                            </Row>

                            <Row>
                              <Col lg={6}>
                                {/* City */}
                                <div className="mb-3">
                                  <Label className="form-label">City</Label>
                                  <Input
                                    name="city"
                                    className="form-control"
                                    placeholder="Enter city"
                                    type="text"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.city || ""}
                                    invalid={
                                      validation.touched.city && validation.errors.city ? true : false
                                    }
                                  />
                                  {validation.touched.city && validation.errors.city ? (
                                    <FormFeedback type="invalid">{validation.errors.city}</FormFeedback>
                                  ) : null}
                                </div>
                              </Col>

                              <Col lg={6}>
                                {/* State */}
                                <div className="mb-3">
                                  <Label className="form-label">State</Label>
                                  <Input
                                    name="state"
                                    className="form-control"
                                    placeholder="Enter state"
                                    type="text"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.state || ""}
                                    invalid={
                                      validation.touched.state && validation.errors.state ? true : false
                                    }
                                  />
                                  {validation.touched.state && validation.errors.state ? (
                                    <FormFeedback type="invalid">{validation.errors.state}</FormFeedback>
                                  ) : null}
                                </div>
                              </Col>
                            </Row>

                            <Row>
                              <Col lg={6}>
                                {/* Postal Code */}
                                <div className="mb-3">
                                  <Label className="form-label">Postal Code</Label>
                                  <Input
                                    name="postalCode"
                                    className="form-control"
                                    placeholder="Enter postal code"
                                    type="text"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.postalCode || ""}
                                    invalid={
                                      validation.touched.postalCode && validation.errors.postalCode ? true : false
                                    }
                                  />
                                  {validation.touched.postalCode && validation.errors.postalCode ? (
                                    <FormFeedback type="invalid">{validation.errors.postalCode}</FormFeedback>
                                  ) : null}
                                </div>
                              </Col>

                              <Col lg={6}>
                                {/* Country */}
                                <div className="mb-3">
                                  <Label className="form-label">Country</Label>
                                  <Input
                                    name="country"
                                    className="form-control"
                                    placeholder="Enter country"
                                    type="text"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.country || ""}
                                    invalid={
                                      validation.touched.country && validation.errors.country ? true : false
                                    }
                                  />
                                  {validation.touched.country && validation.errors.country ? (
                                    <FormFeedback type="invalid">{validation.errors.country}</FormFeedback>
                                  ) : null}
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </Col>

                        <Col lg={6}>
                          {/* Contact Information Section - 2 columns per row */}
                          <div className="mb-4">
                            <h5 className="mb-3">Contact Information</h5>
                            
                            <Row>
                              <Col lg={12}>
                                {/* Contact Name */}
                                <div className="mb-3">
                                  <Label className="form-label">Contact Name</Label>
                                  <Input
                                    name="contactName"
                                    className="form-control"
                                    placeholder="Enter contact name"
                                    type="text"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.contactName || ""}
                                    invalid={
                                      validation.touched.contactName && validation.errors.contactName ? true : false
                                    }
                                  />
                                  {validation.touched.contactName && validation.errors.contactName ? (
                                    <FormFeedback type="invalid">{validation.errors.contactName}</FormFeedback>
                                  ) : null}
                                </div>
                              </Col>
                            </Row>

                            <Row>
                              <Col lg={12}>
                                {/* Contact Email */}
                                <div className="mb-3">
                                  <Label className="form-label">Contact Email</Label>
                                  <Input
                                    name="contactEmail"
                                    className="form-control"
                                    placeholder="Enter contact email"
                                    type="email"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.contactEmail || ""}
                                    invalid={
                                      validation.touched.contactEmail && validation.errors.contactEmail ? true : false
                                    }
                                  />
                                  {validation.touched.contactEmail && validation.errors.contactEmail ? (
                                    <FormFeedback type="invalid">{validation.errors.contactEmail}</FormFeedback>
                                  ) : null}
                                </div>
                              </Col>
                            </Row>

                            <Row>
                              <Col lg={12}>
                                {/* Contact Phone */}
                                <div className="mb-3">
                                  <Label className="form-label">Contact Phone</Label>
                                  <Input
                                    name="contactPhoneNo"
                                    className="form-control"
                                    placeholder="Enter contact phone number"
                                    type="text"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    value={validation.values.contactPhoneNo || ""}
                                    invalid={
                                      validation.touched.contactPhoneNo && validation.errors.contactPhoneNo ? true : false
                                    }
                                  />
                                  {validation.touched.contactPhoneNo && validation.errors.contactPhoneNo ? (
                                    <FormFeedback type="invalid">{validation.errors.contactPhoneNo}</FormFeedback>
                                  ) : null}
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                      </Row>
                    
                      <div className="text-center mt-4">
                        <Button type="submit" color="primary" size="lg" disabled={saving}>
                          {saving && <Spinner size="sm" className="me-2">Loading...</Spinner>}
                          {saving ? 'Updating...' : 'Update Company Profile'}
                        </Button>
                      </div>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CompanyProfile;